// Package imports:
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
import Link from '../../../ui-elements/Link/Link';
import Table from '../../../ui-elements/Table/Table';
import Loading from '../../../ui-elements/Loading/Loading';
import Alert from '../../../ui-elements/Alert/Alert';
import VerticalTable from '../../../ui-elements/Table/VerticalTable';
import Button from '../../../ui-elements/Button/Button';
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { convertToPercentage, formatNumber, getTableDate, insertDash, isCompany } from '../../../services/utils';
import { useBuyingProcess } from '../../../services/buyhook';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { CompanyDetailedOverview, ICompanyReportModel } from '../../../types/CompanyTypes';
import { IKeldanApiResponse } from '../../../types/KeldanTypes';
import { Fetched } from '../../../types/Types';

const boardMemberTypes = ['4', '5', '6', '7'];

interface CompanyDetailedOverviewWrapper {
    eventDate: string,
    overview: CompanyDetailedOverview
}
const ViewMembersReport: React.FC<ICompanyReportModel>= ({
    id,
    eventId
}) => {
    const [ data, setData] = useState<Fetched<CompanyDetailedOverviewWrapper>>(null);
    const [ boardOnly, setBoardOnly] = useState<boolean>(false);
    const [ ssn, setSSN ] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const url = `${GET_KELDAN_API_URL()}/Company/GetMemberReport/${id}?eventId=${eventId}`;
                const response = await fetch(url);
                if (response.ok) {
                    if(response.redirected){
                        window.location.href = response.url;
                        return;
                    }
                    const responseBody: IKeldanApiResponse<CompanyDetailedOverviewWrapper> = await response.json();
                    if (responseBody.success && responseBody.result) {
                        setData(responseBody.result);
                        if (responseBody.result.overview.board !== null) {
                            for (let i of responseBody.result.overview.board){
                                if(boardMemberTypes.includes(i.ssn[0])){
                                    setBoardOnly(true);
                                    return;
                                }
                            }
                        }
                    } else if (responseBody.message) {
                        setData(new Error(responseBody.message));
                    } else {
                        setData(new Error(ErrorMessages.OtherShort));
                    }
                } else {
                    setData(new Error(ErrorMessages.RequestFailed));
                }
            } catch (e) {
                setData(new Error(ErrorMessages.NetworkError));
            }
        }
        fetchData();
    }, []);
    
    const { tryPurchaseItem, isItemLoading, resetState, ModalsAndToasts } = useBuyingProcess(
        async () => {
            try {
                const headers = {
                    'Content-Type': 'application/json; charset=utf-8'
                };
                const url = `${GET_KELDAN_API_URL()}/Einstaklingar/Yfirlit-Kaupa/${ssn}`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers
                });
                if (response.ok) {
                    if (response.redirected) {
                        window.location.href = response.url;
                        return;
                    }
                    const responseBody: IKeldanApiResponse = await response.json();
                    if(responseBody.message) {
                        resetState({
                            type: 'alert',
                            headText: responseBody.message
                        });
                    } else {
                        resetState('bad request')
                    }
                } else {
                    resetState('response not ok');
                }
            } catch (e) {
                resetState('network error');
            }
        },
        'þjóðskrá',
        () => setSSN(null)
    );

    const displayAddress = (address: string | undefined, zip: string | undefined, city: string | undefined) => {
        return `${address ?? '-'}, ${zip ?? '-'} ${city ?? '-'}` ;
    }

    const displayTableRow = (
        index: number,
        title?: string,
        flex?: number,
        text?: string,
        addresses?: boolean,
        address?: string,
        zip?: string,
        city?: string,
        ssn?: string
    ) => (
        <tr
            key={index}
            className='lineContainer'
        >
            <th>
                <strong>{(index !== 0 ) ? '' : title}</strong>
            </th>
            <td style={{'flex': flex}}>
                <div className="text-wrapper">
                    {(text)
                        ? (
                            <p className={cx({'nameAndSocial': ssn})}>
                                {text}
                                {ssn && ', ' + insertDash(ssn)}
                            </p>
                        )
                        : (
                            <p>-</p>
                        )
                    }
                    {(addresses) && <p className='address'>{displayAddress(address, zip, city)}</p>}
                </div>
                {(ssn) && (
                    (!isCompany(ssn))
                    ? (
                        <Button 
                            showLoader={isItemLoading(`${ssn};${title}`)}
                            size='sm'
                            buttonType='buy'
                            onClick={() => {
                                if (title) {
                                    tryPurchaseItem(`${ssn};${title}`);
                                    setSSN(ssn);
                                }
                            }}
                        >
                            Kaupa
                        </Button>
                    )
                    : (
                        <Button 
                            size='sm'
                            buttonType='secondary'
                            anchorProps={{href: `/Fyrirtaeki/Yfirlit/${ssn}`}}
                        >
                            Skoða
                        </Button>
                    )
                )}
            </td>
        </tr>
    );
    
    if (!(data instanceof Error) && data?.overview?.company?.name) {
        document.title = `${data.overview.company.name} (${id}) | Hlutafélagaskrá`
    } else {
        document.title = `Hlutafélagaskrá`
    }
    if (data instanceof Error) return <Alert type='error' headText={data.message} />
    if (data === null) return <Loading />

    return (
        <DisplayBox
            title='Grunnupplýsingar'
            className='KCL_ViewCompanyMembersReport'
            asideComponent={(data === null || data instanceof Error)
                ? undefined
                : (
                    <div className='memberReport-aside-component'>
                        <div className='link-wrapper'>
                            <FontAwesomeIcon icon={faFilePdf} className="icon-fa" />
                            <Link
                                targetBlank
                                linkSize='15'
                                url={(data.overview)
                                    ? `/Pdf/Hlutafelagaskra?ssn=${id}&eventId=${eventId}`
                                    : '#'
                                }
                            >
                                Sækja skýrslu
                            </Link>
                        </div>
                        <div className='date-of-purchase'>
                            Skýrsla keypt: {data.eventDate}
                        </div>
                    </div>
                )
            }
        >
            <div className='vertical-table-wrapper'>
                <VerticalTable
                    fallbackSymbol
                    data={[data.overview]}
                    columns={[{
                        title: 'Nafn',
                        dataAccessor: ({company: {name}}) => name
                    }, {
                        title: 'Kennitala',
                        dataAccessor: ({company: {ssn}}) => insertDash(ssn),
                    }, {
                        title: 'Lögheimili',
                        dataAccessor: ({company: {address, zip, city}}) =>  displayAddress(address, zip, city) ,
                    }, {
                        title: 'Síðast breytt í fyrirtækjaskrá RSK',
                        dataAccessor: ({company: {lastChangeRSK}}) => getTableDate(new Date(lastChangeRSK), 'DD/MM/YYYY', '.'),
                    }, {
                        title: 'Atvinnugrein (ISAT)',
                        dataAccessor: ({isats}) => isats ? `${isats[0].isat ?? '-'} ${isats[0].description ?? isats[0].description_en ?? '-'} ` : '-',
                    }, {
                        title: 'Tilgangur',
                        dataAccessor: ({company: {description}}) => description,
                    }, {
                        title: 'Rekstrarform',
                        dataAccessor: ({company :{formDescription, form}}) => `${formDescription ?? '-'} (${form ?? '-'})`,
                    }, {
                        title: 'Stofnað',
                        dataAccessor: ({company: {dateRegistered}}) => getTableDate(new Date(dateRegistered), 'DD/MM/YYYY', '.'),
                    }, {
                        title: 'Hlutafé',
                        dataAccessor: ({company: {capital, currency}}) => `${formatNumber(capital, '-', 0)} ${currency ?? '-'}`,
                    }, {
                        title: 'Dags. samþykkta',
                        dataAccessor: ({company: {dateAccepted}}) => getTableDate(new Date(dateAccepted), 'DD/MM/YYYY', '.'),
                    }
                ]}
                    tableSize="lg"
                />

               <VerticalTable
                    fallbackSymbol
                    horizontalFlexSize={2}
                    tableSize="lg"
                    data={[data.overview]}
                    columns={boardOnly
                        ? [{
                            title: 'Síðasta breyting á stjórn',
                            dataAccessor: ({company: {lastBoardMeeting}}) => getTableDate(new Date(lastBoardMeeting), 'DD/MM/YYYY', '.'),
                        }, {
                            title: 'Firmað rita',
                            dataAccessor: ({company: {acceptance}}) => acceptance,
                        }, {
                            title: 'Prókúruumboð',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, procuration}) =>
                                (procuration === null || procuration.length === 0)
                                ? '-'
                                : procuration.map((item, index) => displayTableRow(index, 'Prókúruumboð', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Framkvæmdastjórn',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, managers}) => 
                                (managers === null || managers.length === 0)
                                ? '-'
                                : managers.map((item, index) => displayTableRow(index, 'Framkvæmdastjórn', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Stjórn',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, board}) =>
                                (board === null || board.length === 0)
                                ? '-'
                                : board.map((item, index) => displayTableRow(index, 'Stjórn', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Endurskoðendur',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, accountants}) =>
                                (accountants === null || accountants.length === 0)
                                ? '-'
                                : accountants.map((item, index) => displayTableRow(index, 'Endurskoðendur', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Hömlur á meðferð hlutabréfa',
                            dataAccessor: ({company: {restrictions}}) => restrictions === '1' ? 'Já' : 'Nei',
                        }, {
                            title: 'Innlausnarskylda',
                            dataAccessor: ({company:{duty}}) => duty === '1' ? 'Já' : 'Nei',
                        }, {
                            title: 'Stofnendur',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, founders}) =>
                            (founders === null || founders.length === 0)
                            ? '-'
                            : founders.map((item, index) => displayTableRow(index, 'Stofnendur', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }]
                        : [{
                            title: 'Stjórnarformaður',
                            dataAccessor: ({addresses, boardChairman}) =>
                                (boardChairman === null || [boardChairman].length === 0)
                                ? '-' 
                                : [boardChairman].map((item, index) => displayTableRow(index, 'Stjórnarformaður', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                            overrideDefaultStyle: true,
                        }, {
                            title: 'Meðstjórnendur',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, directors}) =>
                                (directors === null || directors.length === 0)
                                ? '-'
                                : directors.map((item, index) => displayTableRow(index, 'Meðstjórnendur', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Varamenn',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, substitutes}) =>
                                (substitutes === null || substitutes.length === 0)
                                ? '-'
                                : substitutes.map((item, index) => displayTableRow(index, 'Varamenn', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Síðasta breyting á stjórn',
                            dataAccessor: ({company: {lastBoardMeeting}}) => getTableDate(new Date(lastBoardMeeting), 'DD/MM/YYYY', '.'),
                        }, {
                            title: 'Firmað rita',
                            dataAccessor: ({company: {acceptance}}) => acceptance,
                        }, {
                            title: 'Prókúruumboð',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, procuration}) =>
                                (procuration === null || procuration.length === 0)
                                ? '-'
                                : procuration.map((item, index) => displayTableRow(index, 'Prókúruumboð', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Framkvæmdastjórn',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, managers}) => 
                                (managers === null || managers.length === 0)
                                ? '-'
                                : managers.map((item, index) => displayTableRow(index, 'Framkvæmdastjórn', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Endurskoðendur',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, accountants}) =>
                                (accountants === null || accountants.length === 0)
                                ? '-'
                                : accountants.map((item, index) => displayTableRow(index, 'Endurskoðendur', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }, {
                            title: 'Hömlur á meðferð hlutabréfa',
                            dataAccessor: ({company: {restrictions}}) => restrictions === '1' ? 'Já' : 'Nei',
                        }, {
                            title: 'Innlausnarskylda',
                            dataAccessor: ({company:{duty}}) => duty === '1' ? 'Já' : 'Nei',
                        }, {
                            title: 'Stofnendur',
                            overrideDefaultStyle: true,
                            dataAccessor: ({addresses, founders}) =>
                            (founders === null || founders.length === 0)
                            ? '-'
                            : founders.map((item, index) => displayTableRow(index, 'Stofnendur', 2, item.name, addresses, item.address, item.zip, item.city, item.ssn)),
                        }]
                    }
                />
            </div>

            {(data.overview.owners !== null) && (
                 <div>
                    <h4>Raunverulegir eigendur</h4>
                    <Table 
                        data={data.overview.owners}
                        columns={[{
                            title: 'Nafn',
                            dataAccessor: ({name}) => name,
                            textAlign: 'left',
                        }, {
                            title: 'Fæðingarár/mán.',
                            dataAccessor: ({faedingarManudur}) => faedingarManudur,
                            textAlign: 'right',
                        }, {
                            title: 'Búsetuland',
                            dataAccessor: ({busetuLand}) => busetuLand,
                            textAlign: 'right',
                        }, {
                            title: 'Ríkisfang',
                            dataAccessor: ({nationality}) => nationality,
                            textAlign: 'right',
                        }, {
                            title: 'Eignarhlutur',
                            dataAccessor: ({ownedPercentage}) => convertToPercentage(ownedPercentage / 100, true, 2),
                            textAlign: 'right',
                        }]}
                        tableSize='lg'
                    />
                </div>
            )}
            <ModalsAndToasts />
        </DisplayBox>
    );
}

export default ViewMembersReport;